import {
  IconAlertCircle, IconCircleCheck, IconCircleX, IconX,
} from "@tabler/icons-react";
import React, { useEffect } from "react";

import zIndex from "../utils/zIndex";

export enum ToastLevel {
  info = "info",
  success = "success",
  warning = "warning",
  error = "error",
  default = "default",
}

interface ToastCloseButtonProps {
  closeToast: (e: React.MouseEvent<HTMLElement>) => void;
  type: ToastLevel | `${ToastLevel}`;
  ariaLabel?: string;
  theme?: any;
}

export interface ToastProps {
  autoClose?: number | false;
  toastId: string | number;

  // Not actually sure what this does but react-toastify includes it in the API. It
  // doesn't do anything.
  closeToast?: () => void;

  // We're going to ignore draggable info for now.
  // Position is taken care of by container
  deleteToast?: () => void;
  type?: ToastLevel | `${ToastLevel}`;

  closeButton: boolean | React.ReactElement<ToastCloseButtonProps> | ((props: ToastCloseButtonProps) => React.ReactNode);
  icon: React.ReactNode | ((props: { type: ToastLevel | `${ToastLevel}`, theme?: any; }) => React.ReactNode) | null;
  pauseOnHover: boolean;
  pauseOnFocusLoss: boolean;
  closeOnClick: boolean;
}

const colors: Record<ToastLevel, string> = {
  [ToastLevel.info]: "bg-bg-inverted text-text-on-color",
  [ToastLevel.success]: "bg-bg-success text-text-on-color",
  [ToastLevel.warning]: "bg-bg-warning text-text-on-color",
  [ToastLevel.error]: "bg-bg-danger text-text-on-color",
  [ToastLevel.default]: "bg-bg-inverted text-text-on-color",
};

const icons: Record<ToastLevel, React.ReactNode> = {
  [ToastLevel.info]: <IconAlertCircle />,
  [ToastLevel.success]: <IconCircleCheck />,
  [ToastLevel.warning]: <IconAlertCircle />,
  [ToastLevel.error]: <IconCircleX />,
  [ToastLevel.default]: <IconAlertCircle />,
};

/** Attempts to match the react-toastify API as closely as possible */
const Toast: React.FC<React.PropsWithChildren<ToastProps>> = ({
  autoClose,
  toastId,
  closeToast,
  deleteToast,
  type = ToastLevel.default,
  children,
  // Default true as per react-toastify spec.
  pauseOnHover = true,
  // Default true as per react-toastify spec.
  pauseOnFocusLoss = true,
  icon,
  // Default true as per react-toastify spec.
  closeButton = true,
  // Default true as per react-toastify spec.
  closeOnClick = true,
}) => {
  const toastRef = React.useRef<HTMLDivElement>(null);
  const timeoutRef = React.useRef<number | null>(null);

  const createTimeout = () => {
    if (autoClose) {
      timeoutRef.current = window.setTimeout(() => {
        handleCloseToast();
      }, autoClose);
    }
  };

  const handleCloseToast = React.useCallback(() => {
    closeToast?.();
    deleteToast?.();
  }, [closeToast, deleteToast]);

  useEffect(() => {
    createTimeout();

    const handleClearTimeout = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };

    const handleCreateTimeout = () => {
      createTimeout();
    };

    if (pauseOnHover) {
      toastRef.current?.addEventListener("mouseenter", handleClearTimeout);
      toastRef.current?.addEventListener("mouseleave", handleCreateTimeout);
    }
    if (pauseOnFocusLoss) {
      window.addEventListener("blur", handleClearTimeout);
      window.addEventListener("focus", handleCreateTimeout);
    }
    return () => {
      toastRef.current?.removeEventListener("mouseenter", handleClearTimeout);
      toastRef.current?.removeEventListener("mouseleave", handleCreateTimeout);
      window.removeEventListener("blur", handleClearTimeout);
      window.removeEventListener("focus", handleCreateTimeout);
    };
  }, [toastRef.current]);

  const CloseButton = React.useCallback(() => {
    if (closeButton === false) {
      return null;
    }

    if (typeof closeButton === "function") {
      return closeButton({ type, closeToast: handleCloseToast });
    }

    if (React.isValidElement(closeButton)) {
      return React.cloneElement(closeButton, { type, closeToast: handleCloseToast });
    }

    return (
      <button
        className="pl-2 cursor-pointer"
        type="button"
        onClick={() => {
          handleCloseToast();
        }}
      >
        <IconX />
      </button>
    );
  }, [closeButton, type, handleCloseToast]);

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <div
      ref={toastRef}
      id={toastId?.toString()}
      className={`flex w-fit p-3 rounded-lg shadow-md ${colors[type]} ${
        closeOnClick ? "cursor-pointer" : ""
      }`}
      onClick={() => {
        if (closeOnClick) {
          handleCloseToast();
        }
      }}
      style={{
        zIndex: zIndex.toast,
      }}
    >
      <span className="pr-2">
        {(typeof icon === "function" ? icon({ type }) : icon) || icons[type]}
      </span>
      <span className="typography-medium-base m-auto">{children}</span>
      {closeButton !== false && (
        <CloseButton />
      )}
    </div>
  );
};

export default Toast;
